/* import __COLOCATED_TEMPLATE__ from './invite-creators.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask, task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { debounce } from '@ember/runloop';
import { closeModal } from 'sh-www/utils/modal-controls';
import Validator from 'validator';
import { COMPANY } from 'sh-www/models/company';

const CLASS_NAME = 'SH-c-invite-creators-modal';

class InviteCreatorsModal extends Component {
  @service ('commands/company-commands') companyCommandService;
  @service ('commands/user-commands') userCommandService;
  @service('error-tracker') errorTracker;
  @service session;

  @tracked formInputs = {
    first_name: '',
    last_name: '',
    email: ''
  };
  @tracked inviteRequestReason = '';
  @tracked existingUserSlug = null;
  @tracked showEmailError = false;
  @tracked error = null;

  get className() {
    return CLASS_NAME;
  }

  get isModalLoading() {
    return this.sendInvite.isRunning || this.sendInviteRequest.isRunning || this.getExistingUser.isRunning;
  }

  get companyTier() {
    return this.session.currentUser.company.tier ?? COMPANY.TIERS.FREE;
  }

  // Company can submit a request to add an already existing seller if the company is Subscription (Business, Enterprise, or Pro)
  get submittingOverrideRequest() {
    return this.existingUserId && COMPANY.TIERS.FREE !== this.companyTier;
  }

  get errorMessage() {
    const { error } = this;
    if (!error) return '';

    const inviteesError = error.errors?.['invitees']?.[0];
    if (inviteesError) {
      return (inviteesError.charAt(inviteesError.length - 1 ) != '.') ?
        `${inviteesError}.`
        : inviteesError;
    }
    return 'There was an error sending the invite. Please try again.'
  }

  get overrideRequestErrorMessage() {
    const overrideRequestError = this.sendInviteRequest.last?.error;
    if (!overrideRequestError) {
      return '';
    }

    const creatorIdError = overrideRequestError.errors?.['user_id']?.[0];
    if (creatorIdError) {
      return (creatorIdError.charAt(creatorIdError.length - 1 ) != '.') ?
        `${creatorIdError}.`
        : creatorIdError;
    }
    return 'There was an error sending the request.'
  }

  get existingUserId() {
    return this.error?.errors?.['user_id']?.[0] ?? null;
  }

  get inviteMax() {
    const { currentUser } = this.session;
    return currentUser.company?.inviteMax ?? Number.POSITIVE_INFINITY;
  }

  get inviteSoftLimitReached() {
    const { currentUser } = this.session;
    if (!currentUser.company) return false;
    return this.args.model.networkSize >= this.inviteMax;
  }

  get inviteHardLimitReached() {
    const { currentUser } = this.session;
    if (!currentUser.company) return false;
    return (
      currentUser.company.tierVersion === COMPANY.TIER_VERSIONS.TWO
      || !currentUser.company.usesPaidTier
    ) && this.args.model.networkSize >= this.inviteMax;
  }

  get submitDisabled() {
    return this.inviteHardLimitReached || this.showEmailError || this.formInputs.email === '' || this.submittingOverrideRequest;
  }

  get requestSubmitDisabled() {
    return this.sendInviteRequest.isRunning || this.inviteRequestReason === '';
  }

  @task
  *sendInvite() {
    this.error = null;
    try {
      yield this.companyCommandService.inviteSellersToCompany(this.session.currentUser.company, [this.formInputs]);
      this.args.model.onInviteSucceeded();
      closeModal(this);
    } catch (e) {
      this.error = e;
      if (this.submittingOverrideRequest) {
        this.getExistingUser.perform();
      }
    }
  }

  @task
  *sendInviteRequest() {
    try {
      yield this.companyCommandService.inviteSellerToCompanyRequest(this.session.currentUser.company.id, this.existingUserId, this.inviteRequestReason);
      this.args.model.onInviteRequestSucceeded();
      closeModal(this);
    } catch (e) {
      this.errorTracker.error('sendInviteRequest: failed to send invite override request', this, e);
      throw e;
    }
  }

  @dropTask
  *getExistingUser() {
    try {
      const response = yield this.userCommandService.getUser(this.existingUserId);
      this.existingUserSlug = response.slug;
    } catch (e) {
      this.errorTracker.error('getExistingUser: failed to load user', this, e);
    }
  }

  @action
  handleInput(field, value) {
    this.formInputs = {
      ...this.formInputs,
      [field]: value,
    };

    if (field === 'email') {
      debounce(this, this.validateEmail, this.formInputs.email, 500);
    }
  }

  @action
  handleInviteRequestReasonInput(value) {
    this.inviteRequestReason = value;
  }

  @action
  validateEmail(email) {
    this.showEmailError = !Validator.isEmail(email);
  }

  @action
  handleDismiss() {
    closeModal(this);
  }
}

export default InviteCreatorsModal;
